//#region types
import { FormEnums } from "models/enums";
import { TimeReportingProjectFormTypes } from "models/formTypes";
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
} from "models/propTypes/genericFormProps";
import { TimeReportingProjectCategoryFormViewProps } from "models/propTypes/timeReportingProjectPropTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
import LabelField from "../../_partial/labelField";
import MemberBillingDetailsField from "../../_partial/memberBillingDetailsField";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

function useCategoryGeneralFields(
  props: TimeReportingProjectCategoryFormViewProps
): FormFieldDefinitionType<TimeReportingProjectFormTypes.ProjectCategory> {
  const {
    earliestStartDate,
    latestEndDate,
    otherDeviationTypes,
    onSelectOtherDeviation,
    isAdjustPreviouslyReportedTimeHidden,
    isShouldEffectSalaryPayoutHidden,
  } = props;
  return {
    name: {
      label: TranslationKeys.TimeReportingProject.CategoryName,
      controlLabel: " ",
      type: FormEnums.FormFieldType.TEXT,
    },
    //TODO: apply this once invoice is compatible with this functionality
    // billingModel: {
    //   label: TranslationKeys.TimeReportingProject.CategoryBillingModel,
    //   type: FormEnums.FormFieldType.CHOICE,
    //   extraProps: {
    //     items: billingModels,
    //   },
    // },
    defaultHourlyRate: {
      label: TranslationKeys.TimeReportingProject.CategoryCommonHourlyRate,
      controlLabel: TranslationKeys.TimeReportingProject.CategoryCommonHourlyRateControlLabel,
      descriptionText: TranslationKeys.TimeReportingProject.CategoryCommonDescriptionText,
      type: FormEnums.FormFieldType.CURRENCY,
      onChangeHandler: props.onDefaultHourlyRateChange,
    },
    endDate: {
      label: TranslationKeys.TimeReportingProject.MemberEndDate,
      type: FormEnums.FormFieldType.DATE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) => !values.endDate,
      extraProps: {
        minDate: earliestStartDate,
        maxDate: latestEndDate,
      },
    },
    categoryMembers: {
      label: TranslationKeys.TimeReportingProject.ProjectMemberAccesToCategory,
      type: FormEnums.FormFieldType.EDITABLETABLE,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) =>
        !values.categoryMembers || values.categoryMembers.length === 0,
      innerFields: {
        selected: {
          label: "",
          type: FormEnums.FormFieldType.CHECKBOX,
          classes: {
            formControl: cssExports.select,
          },
          onChangeHandler: props.onMemberActivationChange,
        },
        name: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <MemberBillingDetailsField currency={props.project?.currency}></MemberBillingDetailsField>,
          readonly: true,
        },
        accessUntilText: {
          label: "",
          type: FormEnums.FormFieldType.CUSTOM,
          customField: <LabelField></LabelField>,
          extraProps: { hideValueIfReadOnly: true },
          readonly: (values: TimeReportingProjectFormTypes.CategoryMember) => !values.accessUntil,
          rightAlign: true,
          classes: { formControl: cssExports.validFromFieldContainer },
        },
        accessUntil: {
          label: "",
          type: FormEnums.FormFieldType.MONTH,
          extraProps: {
            popperPositionFixed: true,
            hideDateIfReadOnly: true,
            hideClearable: true,
            setLastDayOfMonth: true,
          },
          readonly: (values: TimeReportingProjectFormTypes.CategoryMember) => !values.accessUntil,
        },
      },
      className: cssExports.categoryMemeberContainer,
      extraProps: {
        isCompactTable: true,
        selectionFieldName: "selected",
        hideActions: true,
        showColumnHeaders: true,
        onItemSelectionChange: props.onMemberSelectAllActivationChange,
        headers: [
          {
            label: "",
            fieldName: "selected",
          },
          {
            fieldName: "name",
            label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReportingProject.IncludeAll),
            columnSpan: 3,
          },
        ],
      } as EditableTableFieldProps<TimeReportingProjectFormTypes.ProjectCategory>,
    },
    shouldEffectSalaryPayout: {
      label: TranslationKeys.TimeReportingProject.ShouldEffectSalary,
      descriptionText: TranslationKeys.TimeReportingProject.ShouldEffectSalaryDescription,
      type: FormEnums.FormFieldType.TOGGLE,
      hidden: isShouldEffectSalaryPayoutHidden,
    },
    connectedDeviationTypeName: {
      type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
      label: TranslationKeys.TimeReportingProject.DeviationType,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) => !values.shouldEffectSalaryPayout,
      innerFields: {
        connectedDeviationTypeName: {
          label: TranslationKeys.TimeReportingProject.DeviationType,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          descriptionText: TranslationKeys.TimeReportingProject.DeviationTypeDescription,
          widthColumnCount: 8,
          hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) => !values.shouldEffectSalaryPayout,
          extraProps: {
            referenceFieldName: FormFieldConstants.TimeReportingProject.ProjectCategory.ConnectedDeviationType,
            onItemSelect: onSelectOtherDeviation,
            items: otherDeviationTypes?.map(
              (item): AutocompleteListItem => ({
                id: item,
                value: LocalizationHelperInstance.getFormattedMessage(`SalaryDeviation.Type_${item}`),
              })
            ),
          } as AutoCompleteExtraProps<TimeReportingProjectFormTypes.ProjectCategory>,
        },
      },
    },
    deviationComment: {
      label: TranslationKeys.Common.Comment,
      type: FormEnums.FormFieldType.TEXTAREA,
      hidden: (values: TimeReportingProjectFormTypes.ProjectCategory) => !values.shouldEffectSalaryPayout,
    },
    adjustPreviouslyReportedTime: {
      label: TranslationKeys.TimeReportingProject.AdjustPreviouslyReportedTime,
      descriptionText: TranslationKeys.TimeReportingProject.AdjustPreviouslyReportedTimeDescription,
      type: FormEnums.FormFieldType.TOGGLE,
      hidden: isAdjustPreviouslyReportedTimeHidden,
    },
  };
}

export default useCategoryGeneralFields;
